// ── ProductForm custom element ────────────────────────────────────────────────
// Handles text-length validation and add-to-cart for non-image products.
// Image products (those with file inputs) are handled by cart-uploader.js.

if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector("span");

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
        this.dynamicCheckoutEl = this.querySelector(
          '[data-shopify="payment-button"]',
        );

        // Character-limit setup (per-variant metafield custom.caracter_maximos)
        this.variantCharLimits = this.parseVariantCharLimits();
        this.textArea = this.form.querySelector(
          '[name="properties[Texto personalizado]"]',
        );
        this.textContainer =
          this.textArea?.closest(".customization-container") ?? null;
        this.charLimitInfoEl = this.querySelector('[id^="CharLimitInfo-"]');
        this.charCountEl = this.querySelector('[id^="CharCount-"]');
        this.charLimitErrorEl = this.querySelector('[id^="CharLimitError-"]');
        this.currentCharLimit = 0;
        this.textTooLong = false;

        const initialVariantId = String(this.variantIdInput?.value ?? "");
        if (initialVariantId && this.variantCharLimits[initialVariantId]) {
          const { limit, title } = this.variantCharLimits[initialVariantId];
          this.currentCharLimit = limit;
          this.updateCharLimitDisplay(title, limit);
        }

        if (this.textArea) {
          this.textArea.addEventListener(
            "input",
            this.onTextAreaInput.bind(this),
          );
        }

        if (typeof subscribe === "function") {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            (event) => {
              if (event.data.sectionId !== this.dataset.sectionId) return;
              const variantId = String(event.data.variant.id);
              const data = this.variantCharLimits[variantId];
              this.currentCharLimit = data?.limit ?? 0;
              this.updateCharLimitDisplay(
                event.data.variant.title,
                this.currentCharLimit,
              );
            },
          );
        }
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      // ── Validation state ───────────────────────────────────────────────────────

      setValidationDisabled(shouldDisable) {
        this.submitButton.classList.toggle("validation-disabled", shouldDisable);
        // Do not touch .disabled here — that is managed by toggleSubmitButton()
        // for availability (sold-out / unavailable variant). Clearing it here
        // would incorrectly re-enable the button after a char-limit correction.
        if (this.dynamicCheckoutEl) {
          this.dynamicCheckoutEl.classList.toggle(
            "payment-btn-disabled",
            shouldDisable,
          );
        }
      }

      // ── Character-limit helpers ────────────────────────────────────────────────

      parseVariantCharLimits() {
        const scriptEl = document.getElementById(
          `VariantCharLimits-${this.dataset.sectionId}`,
        );
        if (!scriptEl) return {};
        try {
          return JSON.parse(scriptEl.textContent);
        } catch (e) {
          return {};
        }
      }

      updateCharLimitDisplay(title, limit) {
        if (this.charLimitInfoEl) {
          if (limit > 0) {
            const size = title.split(" / ")[0].trim();
            const escapedSize = size
              .replace(/&/g, "&amp;")
              .replace(/</g, "&lt;")
              .replace(/>/g, "&gt;")
              .replace(/"/g, "&quot;");
            const template =
              window.customTextStrings?.maxCharsHtml ||
              "El n&uacute;mero m&aacute;ximo de caracteres para <strong>[size]</strong> es de <strong>[limit]</strong>.";
            this.charLimitInfoEl.innerHTML = template
              .replace("[size]", escapedSize)
              .replace("[limit]", limit);
          } else {
            this.charLimitInfoEl.innerHTML = "";
          }
        }
        this.validateTextLength();
      }

      onTextAreaInput() {
        this.validateTextLength();
      }

      validateTextLength() {
        if (!this.textArea) return true;
        const limit = this.currentCharLimit;
        const text = this.textArea.value;

        if (!limit || limit <= 0) {
          this.textTooLong = false;
          if (this.charCountEl) this.charCountEl.textContent = "";
          return true;
        }

        const exceeded = text.length > limit;
        this.textTooLong = exceeded;
        const remaining = limit - text.length;

        if (this.charCountEl) {
          this.charCountEl.textContent =
            text.length > 0 ? `${text.length}/${limit}` : "";
          this.charCountEl.classList.toggle("char-count--error", exceeded);
          this.charCountEl.classList.toggle(
            "char-count--warning",
            !exceeded && remaining < 20,
          );
          this.charCountEl.classList.toggle(
            "char-count--ok",
            !exceeded && remaining >= 20,
          );
        }
        if (this.textContainer) {
          this.textContainer.classList.toggle("error-state", exceeded);
        }
        if (this.charLimitErrorEl) {
          if (exceeded) {
            const over = text.length - limit;
            const exceededTemplate =
              window.customTextStrings?.exceeded ||
              "‼️ Has superado el límite de [limit] caracteres ([over] de más).";
            this.charLimitErrorEl.textContent = exceededTemplate
              .replace("[limit]", limit)
              .replace("[over]", over);
            this.charLimitErrorEl.hidden = false;
          } else {
            this.charLimitErrorEl.hidden = true;
          }
        }

        this.setValidationDisabled(exceeded);
        return !exceeded;
      }

      // ── Submit handler ─────────────────────────────────────────────────────────

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (this.submitButton.classList.contains("validation-disabled")) {
          if (this.textTooLong && this.textArea) {
            this.textArea.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
            });
            this.textArea.focus();
          }
          return;
        }

        if (!this.validateTextLength()) {
          if (this.textArea) {
            this.textArea.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
            });
            this.textArea.focus();
          }
          return;
        }

        this.handleErrorMessage();

        // Image products: cart-uploader.js intercepts submit, compresses and
        // uploads files, then calls /cart/add. Skip _addToCart here.
        if (this.form.querySelector('input[type="file"]')) return;

        this._addToCart();
      }

      // ── Cart submission (non-image products only) ──────────────────────────────

      _addToCart() {
        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            "sections",
            this.cart
              .getSectionsToRender()
              .map((s) => s.section || s.id)
              .filter(Boolean)
              .join(","),
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        this.submitButton.setAttribute("aria-disabled", "true");
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner")?.classList.remove("hidden");

        fetch(routes.cart_add_url, config)
          .then((response) => {
            if (!response.ok) {
              return response.text().then((text) => {
                let shopifyMsg;
                try {
                  const data = JSON.parse(text);
                  shopifyMsg = data.description || data.message || data.error;
                } catch (_) {}
                const err = new Error(shopifyMsg || `HTTP ${response.status}`);
                err.status = response.status;
                throw err;
              });
            }
            return response.json();
          })
          .then((response) => {
            if (response.status) {
              if (typeof publish === "function") {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: "product-form",
                  productVariantId: formData.get("id"),
                  errors: response.errors || response.description,
                  message: response.message,
                });
              }
              this.handleErrorMessage(
                (response.description ||
                  response.message ||
                  window.customTextStrings?.cartAddError ||
                  "No se pudo añadir al carrito.") +
                  " " +
                  (window.customTextStrings?.cartAddErrorHelp ||
                    "Si el problema persiste, escríbenos y te ayudamos."),
              );
              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (typeof publish === "function") {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response,
              });
            }

            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true },
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            this.handleErrorMessage(
              window.customTextStrings?.cartAddErrorRetry ||
                "No se pudo añadir al carrito. Por favor, recarga la página e inténtalo de nuevo. " +
                  "Si el problema persiste, escríbenos y te ayudamos.",
            );
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            this.querySelector(".loading__spinner")?.classList.add("hidden");
            this.submitButton.removeAttribute("aria-disabled");
            // Do not set .disabled = false here — _addToCart never sets it to
            // true, so forcing false could re-enable a sold-out button.
          });
      }

      // ── Error display ──────────────────────────────────────────────────────────

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;
        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message",
          );
        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);
        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          if (this.textTooLong) return;
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    },
  );
}
